import {
  discriminate,
  EngagementReportItem,
  UserField,
  UserFieldType,
  UserFilter,
} from "@coaching-culture/types";
import {
  Button,
  DateRangeButton,
  Flex,
  Loader,
  Panel,
  SortableTable,
  SortableTableColumn,
  UserFlag,
} from "@coaching-culture/ui";
import { sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";

import {
  useCancellationReasons,
  useEngagementReport,
  useUserFields,
} from "@coaching-culture/api-client";
import { useGoalTerminology } from "auth/OrgProvider";
import CenterColumn from "components/CenterColumn";
import { FilterModal } from "components/FilterPanel";
import { PageHeader } from "components/PageHeader";
import { addDays } from "date-fns";
import { Link } from "react-router-dom";
import { saveExportAsCsv } from "utils";

export const EngagementInsights = () => {
  const goalTerms = useGoalTerminology();
  const { data: userFields } = useUserFields();
  const { data: cancellationReasons } = useCancellationReasons();
  const [filter, setFilter] = useState<UserFilter>({
    groups: [],
    userFields: [],
  });
  const [range, setRange] = useState<[Date, Date]>([
    addDays(new Date(), -30),
    new Date(),
  ]);
  const report = useEngagementReport(range[0], range[1], filter);

  const [showFilters, setShowFilters] = useState(false);

  const formatUserField = useCallback(
    (user: EngagementReportItem, userFieldDef: UserField) => {
      const selectFields = (userFields || []).filter(
        discriminate("type", UserFieldType.Select),
      );

      const field = user.userFields.find((uf) => uf.id === userFieldDef.id);
      if (field != null) {
        if (userFieldDef.type === UserFieldType.Select) {
          const selectField = selectFields.find(
            (f) => f.id === userFieldDef.id,
          );
          return (
            selectField.options.find((option) => option.id === field.value)
              ?.name ?? ""
          );
        } else {
          return field.value as string;
        }
      }
      return "";
    },
    [userFields],
  );

  const getUserFieldCols = useMemo(() => {
    return (
      sortBy(userFields ?? [], (x) => x.name).map((y) => ({
        label: y.name,
        headingProps: {
          style: {
            textAlign: "center",
            whiteSpace: "nowrap",
          },
        },
        name: y.id,
        props: {
          style: {
            textAlign: "center",
          },
        },
        sortFormat: (x: EngagementReportItem) => {
          return formatUserField(x, y);
        },
        format: (x: EngagementReportItem) => {
          return formatUserField(x, y);
        },
        exportFormat: (x: EngagementReportItem) => {
          return formatUserField(x, y);
        },
      })) ?? []
    );
  }, [userFields, formatUserField]);

  const getCancellationReasonCols = useMemo(() => {
    return (cancellationReasons ?? []).map((reason) => ({
      label: `Cancelled for: ${reason.text}`,

      name: `cancellation_${reason.id}`,

      format: (x: EngagementReportItem) =>
        x.cancellationReasonCounts.find((r) => r.id === reason.id)?.count ?? 0,
      sortFormat: (x: EngagementReportItem) =>
        x.cancellationReasonCounts.find((r) => r.id === reason.id)?.count ?? 0,
      exportFormat: (x: EngagementReportItem) =>
        String(
          x.cancellationReasonCounts.find((r) => r.id === reason.id)?.count ??
            0,
        ),
      numeric: true,
    }));
  }, [cancellationReasons]);

  const cols = useMemo(
    (): SortableTableColumn<EngagementReportItem>[] => [
      {
        name: "name",
        label: "Name",
        format: (x) => (
          <UserFlag user={x} to={`/success/people/users/${x.id}`} />
        ),
        sortFormat: (x) => x.name.toLowerCase(),
        exportFormat: (x) => x.name,
        excludeFromExport: true,
        filter: {
          type: "search",
          getValue: (x) => [x.name, x.email].join(""),
        },
      },
      {
        name: "firstName",
        label: "First Name",
        hidden: true,
      },
      {
        name: "lastName",
        label: "Last Name",
        hidden: true,
      },
      {
        name: "email",
        label: "Email",
        hidden: true,
      },
      {
        name: "managers",
        label: "Managers",
        format: (x) => (
          <Flex flexDirection={"column"} style={{ gap: 3 }}>
            {x.managers.map((x) => (
              <UserFlag user={x} />
            ))}
          </Flex>
        ),
        sortFormat: (x) => x.managers[0]?.name ?? "",
        exportFormat: (x) => x.managers.map((x) => x.name).join(", "),
        filter: {
          type: "search",
          getValue: (x) => x.managers.map((x) => x.name).join(""),
        },
      },
      {
        name: "directReports",
        label: "Report Count",
        format: (x) => x.directReports.length,
        sortFormat: (x) => x.directReports.length,
        numeric: true,
      },
      {
        name: "groups",
        label: "Groups",
        format: (x) =>
          sortBy(x.groups, "id").map((x, i, self) => (
            <>
              <Link key={x.id} to={`/success/people/groups/${x.id}`}>
                {x.name}
              </Link>
              {i < self.length - 1 && " | "}
            </>
          )),
        exportFormat: (x) => x.groups.map((x) => x.name).join(" | "),
        sortFormat: (x) => x.groups.map((x) => x.name).join(" | "),
      },
      ...getUserFieldCols,
      {
        name: "mindsetCompleted",
        label: "Mindset Completed",
        numeric: true,
      },
      {
        name: "lessonsCompleted",
        label: "Lessons Completed",
        numeric: true,
      },
      {
        name: "assessmentsCompleted",
        label: "Assessments Completed",
        numeric: true,
      },
      {
        name: "learnCastCompleted",
        label: "LearnCast Completed",
        numeric: true,
      },
      {
        name: "conversationsScheduled",
        label: "Conversations Scheduled",
        numeric: true,
      },
      {
        name: "conversationsCompleted",
        label: "Conversations Completed",
        numeric: true,
      },
      {
        name: "conversationsCancelled",
        label: "Conversations Cancelled",
        numeric: true,
      },
      ...getCancellationReasonCols,
      {
        name: "goalsSet",
        label: `${goalTerms.goal.asPluralTitle()} Set`,
        numeric: true,
      },
      {
        name: "goalsCompleted",
        label: `${goalTerms.goal.asPluralTitle()} Completed`,
        numeric: true,
      },
      {
        name: "totalLiveGoals",
        label: "Total Live Goals",
        numeric: true,
      },
      {
        name: "totalGoalsCompleted",
        label: "Total Goals Completed",
        numeric: true,
      },
    ],
    [goalTerms, getUserFieldCols, getCancellationReasonCols],
  );

  const data = report.data;

  const onCancel = (result: UserFilter) => {
    if (result) {
      setFilter(result);
    }

    setShowFilters(false);
  };

  const rowSearch = useCallback((item: EngagementReportItem) => item.name, []);

  return (
    <CenterColumn width="100%">
      <PageHeader
        text="Engagement"
        subtitle="A report on the general engagement of the platform"
        helpIdent="engagement-report"
      >
        <Flex justifyContent={"flex-end"}>
          <Button color="primary" onClick={() => setShowFilters(true)} mr={2}>
            Filters ({filter.groups.length + filter.userFields.length})
          </Button>
          <DateRangeButton value={range} onChange={setRange} noNull />
        </Flex>
      </PageHeader>
      <FilterModal
        isOpen={showFilters}
        filter={filter}
        filteredUsers={(data ?? []).map((x) => x.id)}
        onClose={(result) => {
          onCancel(result);
        }}
      />

      <Panel style={{ overflow: "hidden", position: "relative" }}>
        {(report.isLoading || report.isPreviousData) && <Loader overlay />}
        <SortableTable
          columns={cols}
          data={report.data}
          defaultSort={0}
          rowKey="id"
          paged
          stickyFirstColumn
          onExport={saveExportAsCsv("EngagementReport.csv")}
          rowSearch={rowSearch}
        />
      </Panel>
    </CenterColumn>
  );
};
